import { Box, Grid, GridItem } from '@chakra-ui/react';
import type { SingleValue } from 'chakra-react-select';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import type { BVFormData } from '@/components/Templates/BenefitVerification/BenefitVerificationForm/BenefitVerification';
import { woundTypeMap } from '@/constants';
import {
  useCreateApplicationCode,
  useCreateDiagnosisCode,
  useGetApplicationCodes,
  useGetDiagnosisCodes,
} from '@/hooks/service-hooks/codes';
import { usePaginatedSelect } from '@/hooks/usePaginatedSelect';
import type { ApplicationCode, DiagnosisCode } from '@/services/codes';
import type { FormMethods } from '@/types/form';
import { createEnhancedItemsCore } from '@/utils/bvUtils';
import { handleNumericInputChange as validateNumericInput } from '@/utils/formValidationUtils';
export interface Wound1Props {
  woundNumber: number;
  formMethods: FormMethods<BVFormData>;
}

const Wound2: React.FC<Wound1Props> = ({ woundNumber, formMethods }) => {
  const { watch, errors, setValue, setError } = formMethods;
  const [activeField, setActiveField] = useState<'code1' | 'code2' | null>(null);
  const [activeDiagnosisField, setActiveDiagnosisField] = useState<'diagnosis1' | 'diagnosis2' | 'diagnosis3' | 'diagnosis4' | null>(null);

  // Wrapper function to use the shared validation utility
  const handleNumericInputChange = (
    inputValue: string,
    fieldName: 'ta_wound2_woundlength' | 'ta_wound2_woundwidth' | 'ta_wound2_wounddepth'
  ) => {
    validateNumericInput(inputValue, fieldName, setValue, setError);
  };

  const applicationCodes = usePaginatedSelect({
    queryHook: useGetApplicationCodes,
    queryParams: { enabled: true },
    transform: (code: ApplicationCode) => ({
      value: code.ta_applicationcodesid,
      label: code.ta_name,
    }),
    errorMessage: 'Unable to load application codes',
  });

  // Create application code mutation
  const { mutateAsync: createApplicationCode, isLoading: isCreating } = useCreateApplicationCode(
    async (data: ApplicationCode) => {
      // Immediately create SelectItem from the response and set it as selected
      const newSelectItem: SelectItem = {
        value: data.ta_applicationcodesid,
        label: data.ta_name,
      };

      // Set the value immediately based on active field
      if (activeField === 'code1') {
        setValue('ta_Wound2_Application1@odatabind', newSelectItem);
        setError('ta_Wound2_Application1@odatabind', { type: 'manual', message: '' });
      } else if (activeField === 'code2') {
        setValue('ta_Wound2_Application2@odatabind', newSelectItem);
        setError('ta_Wound2_Application2@odatabind', { type: 'manual', message: '' });
      }
      
      // Reset active field
      setActiveField(null);

    },
    (error) => {
      console.error('Failed to create application code:', error);
      setActiveField(null);
    },
  );

  // Check if there's an exact match in the results
  const hasExactMatch = useMemo(() => {
    if (!applicationCodes.search || applicationCodes.search.trim().length < 5) {
      return false;
    }
    return applicationCodes.items.some(
      (item) => (item.label as string)?.toLowerCase().trim() === applicationCodes.search?.toLowerCase().trim()
    );
  }, [applicationCodes.items, applicationCodes.search]);

  // Get current selected values
  const selectedCode1 = watch('ta_Wound2_Application1@odatabind') as SelectItem | undefined;
  const selectedCode2 = watch('ta_Wound2_Application2@odatabind') as SelectItem | undefined;

  // Check if selected value matches the search text
  const selectedMatchesSearch1 = (selectedCode1?.label as string)?.toLowerCase().trim() === applicationCodes.search?.toLowerCase().trim();
  const selectedMatchesSearch2 = (selectedCode2?.label as string)?.toLowerCase().trim() === applicationCodes.search?.toLowerCase().trim();

  // Create enhanced items with "Add" option for code 1
  const enhancedItemsCode1 = useMemo(() => {
    return createEnhancedItemsCore(
      applicationCodes.items,
      applicationCodes.search || '',
      selectedCode1,
      activeField,
      'code1',
      hasExactMatch,
      selectedMatchesSearch1,
      applicationCodes.loading,
      isCreating,
      (searchLength) => searchLength === 5,
      true, // Allow numeric only
    );
  }, [applicationCodes.items, applicationCodes.search, hasExactMatch, selectedMatchesSearch1, applicationCodes.loading, isCreating, activeField, selectedCode1]);

  // Create enhanced items with "Add" option for code 2
  const enhancedItemsCode2 = useMemo(() => {
    return createEnhancedItemsCore(
      applicationCodes.items,
      applicationCodes.search || '',
      selectedCode2,
      activeField,
      'code2',
      hasExactMatch,
      selectedMatchesSearch2,
      applicationCodes.loading,
      isCreating,
      (searchLength) => searchLength === 5,
      true, // Allow numeric only
    );
  }, [applicationCodes.items, applicationCodes.search, hasExactMatch, selectedMatchesSearch2, applicationCodes.loading, isCreating, activeField, selectedCode2]);

  // Handle selection for code 1
  const handleCode1Change = async (newValue: SingleValue<SelectItem>) => {
    if (!newValue) {
      setValue('ta_Wound2_Application1@odatabind', null as any);
      return;
    }

    // Check if it's the "Add" option
    if ((newValue as any).__isAddOption && typeof newValue.value === 'string' && newValue.value.startsWith('__add__')) {
      const searchText = newValue.value.replace('__add__', '');
      setActiveField('code1');
      try {
        if (watch('ta_Wound2_Application1@odatabind')) {
          setValue('ta_Wound2_Application1@odatabind', null as any);
        }
        await createApplicationCode({ ta_name: searchText });
      } catch (error) {
        // Error is handled in the onError callback
      }
    } else {
      // Regular selection
      setValue('ta_Wound2_Application1@odatabind', newValue as SelectItem);
      setError('ta_Wound2_Application1@odatabind', { type: 'manual', message: '' });
    }
  };

  // Handle selection for code 2
  const handleCode2Change = async (newValue: SingleValue<SelectItem>) => {
    if (!newValue) {
      setValue('ta_Wound2_Application2@odatabind', null as any);
      return;
    }

    // Check if it's the "Add" option
    if ((newValue as any).__isAddOption && typeof newValue.value === 'string' && newValue.value.startsWith('__add__')) {
      const searchText = newValue.value.replace('__add__', '');
      setActiveField('code2');
      try {
        if (watch('ta_Wound2_Application2@odatabind')) {
          setValue('ta_Wound2_Application2@odatabind', null as any);
        }
        await createApplicationCode({ ta_name: searchText });
      } catch (error) {
        // Error is handled in the onError callback
      }
    } else {
      // Regular selection
      setValue('ta_Wound2_Application2@odatabind', newValue as SelectItem);
      setError('ta_Wound2_Application2@odatabind', { type: 'manual', message: '' });
    }
  };

  // Diagnosis codes with pagination
  const diagnosisCodes = usePaginatedSelect({
    queryHook: useGetDiagnosisCodes,
    queryParams: { enabled: true },
    transform: (code: DiagnosisCode) => ({
      value: code.ta_diagnosiscodeid,
      label: code.ta_name,
    }),
    errorMessage: 'Unable to load diagnosis codes',
  });

  // Create diagnosis code mutation
  const { mutateAsync: createDiagnosisCode, isLoading: isCreatingDiagnosis } = useCreateDiagnosisCode(
    async (data: DiagnosisCode) => {
      // Immediately create SelectItem from the response and set it as selected
      const newSelectItem: SelectItem = {
        value: data.ta_diagnosiscodeid,
        label: data.ta_name,
      };

      // Set the value immediately based on active diagnosis field
      if (activeDiagnosisField === 'diagnosis1') {
        setValue('ta_Wound2_Diagnosis1@odatabind', newSelectItem);
        setError('ta_Wound2_Diagnosis1@odatabind', { type: 'manual', message: '' });
      } else if (activeDiagnosisField === 'diagnosis2') {
        setValue('ta_Wound2_Diagnosis2@odatabind', newSelectItem);
        setError('ta_Wound2_Diagnosis2@odatabind', { type: 'manual', message: '' });
      } else if (activeDiagnosisField === 'diagnosis3') {
        setValue('ta_Wound2_Diagnosis3@odatabind', newSelectItem);
        setError('ta_Wound2_Diagnosis3@odatabind', { type: 'manual', message: '' });
      } else if (activeDiagnosisField === 'diagnosis4') {
        setValue('ta_Wound2_Diagnosis4@odatabind', newSelectItem);
        setError('ta_Wound2_Diagnosis4@odatabind', { type: 'manual', message: '' });
      }
      
      // Reset active diagnosis field
      setActiveDiagnosisField(null);

    },
    (error) => {
      console.error('Failed to create diagnosis code:', error);
      setActiveDiagnosisField(null);
    },
  );

  // Check if there's an exact match in the diagnosis results
  const hasExactMatchDiagnosis = useMemo(() => {
    if (!diagnosisCodes.search || diagnosisCodes.search.trim().length < 5) {
      return false;
    }
    return diagnosisCodes.items.some(
      (item) => (item.label as string)?.toLowerCase().trim() === diagnosisCodes.search?.toLowerCase().trim()
    );
  }, [diagnosisCodes.items, diagnosisCodes.search]);

  // Get current selected diagnosis values
  const selectedDiagnosis1 = watch('ta_Wound2_Diagnosis1@odatabind') as SelectItem | undefined;
  const selectedDiagnosis2 = watch('ta_Wound2_Diagnosis2@odatabind') as SelectItem | undefined;
  const selectedDiagnosis3 = watch('ta_Wound2_Diagnosis3@odatabind') as SelectItem | undefined;
  const selectedDiagnosis4 = watch('ta_Wound2_Diagnosis4@odatabind') as SelectItem | undefined;

  // Check if selected diagnosis value matches the search text
  const selectedMatchesSearchDiagnosis1 = (selectedDiagnosis1?.label as string)?.toLowerCase().trim() === diagnosisCodes.search?.toLowerCase().trim();
  const selectedMatchesSearchDiagnosis2 = (selectedDiagnosis2?.label as string)?.toLowerCase().trim() === diagnosisCodes.search?.toLowerCase().trim();
  const selectedMatchesSearchDiagnosis3 = (selectedDiagnosis3?.label as string)?.toLowerCase().trim() === diagnosisCodes.search?.toLowerCase().trim();
  const selectedMatchesSearchDiagnosis4 = (selectedDiagnosis4?.label as string)?.toLowerCase().trim() === diagnosisCodes.search?.toLowerCase().trim();

  // Create enhanced items with "Add" option for diagnosis 1
  const enhancedItemsDiagnosis1 = useMemo(() => {
    return createEnhancedItemsCore(
      diagnosisCodes.items,
      diagnosisCodes.search || '',
      selectedDiagnosis1,
      activeDiagnosisField,
      'diagnosis1',
      hasExactMatchDiagnosis,
      selectedMatchesSearchDiagnosis1,
      diagnosisCodes.loading,
      isCreatingDiagnosis,
      (searchLength) => searchLength >= 3 && searchLength <= 7,
      false, // Allow alphanumeric
    );
  }, [diagnosisCodes.items, diagnosisCodes.search, hasExactMatchDiagnosis, selectedMatchesSearchDiagnosis1, diagnosisCodes.loading, isCreatingDiagnosis, activeDiagnosisField, selectedDiagnosis1]);

  // Create enhanced items with "Add" option for diagnosis 2
  const enhancedItemsDiagnosis2 = useMemo(() => {
    return createEnhancedItemsCore(
      diagnosisCodes.items,
      diagnosisCodes.search || '',
      selectedDiagnosis2,
      activeDiagnosisField,
      'diagnosis2',
      hasExactMatchDiagnosis,
      selectedMatchesSearchDiagnosis2,
      diagnosisCodes.loading,
      isCreatingDiagnosis,
      (searchLength) => searchLength >= 3 && searchLength <= 7,
      false, // Allow alphanumeric
    );
  }, [diagnosisCodes.items, diagnosisCodes.search, hasExactMatchDiagnosis, selectedMatchesSearchDiagnosis2, diagnosisCodes.loading, isCreatingDiagnosis, activeDiagnosisField, selectedDiagnosis2]);

  // Create enhanced items with "Add" option for diagnosis 3
  const enhancedItemsDiagnosis3 = useMemo(() => {
    return createEnhancedItemsCore(
      diagnosisCodes.items,
      diagnosisCodes.search || '',
      selectedDiagnosis3,
      activeDiagnosisField,
      'diagnosis3',
      hasExactMatchDiagnosis,
      selectedMatchesSearchDiagnosis3,
      diagnosisCodes.loading,
      isCreatingDiagnosis,
      (searchLength) => searchLength >= 3 && searchLength <= 7,
      false, // Allow alphanumeric
    );
  }, [diagnosisCodes.items, diagnosisCodes.search, hasExactMatchDiagnosis, selectedMatchesSearchDiagnosis3, diagnosisCodes.loading, isCreatingDiagnosis, activeDiagnosisField, selectedDiagnosis3]);

  // Create enhanced items with "Add" option for diagnosis 4
  const enhancedItemsDiagnosis4 = useMemo(() => {
    return createEnhancedItemsCore(
      diagnosisCodes.items,
      diagnosisCodes.search || '',
      selectedDiagnosis4,
      activeDiagnosisField,
      'diagnosis4',
      hasExactMatchDiagnosis,
      selectedMatchesSearchDiagnosis4,
      diagnosisCodes.loading,
      isCreatingDiagnosis,
      (searchLength) => searchLength >= 3 && searchLength <= 7,
      false, // Allow alphanumeric
    );
  }, [diagnosisCodes.items, diagnosisCodes.search, hasExactMatchDiagnosis, selectedMatchesSearchDiagnosis4, diagnosisCodes.loading, isCreatingDiagnosis, activeDiagnosisField, selectedDiagnosis4]);

  // Handle selection for diagnosis 1
  const handleDiagnosis1Change = async (newValue: SingleValue<SelectItem>) => {
    if (!newValue) {
      setValue('ta_Wound2_Diagnosis1@odatabind', null as any);
      return;
    }

    if ((newValue as any).__isAddOption && typeof newValue.value === 'string' && newValue.value.startsWith('__add__')) {
      const searchText = newValue.value.replace('__add__', '');
      setActiveDiagnosisField('diagnosis1');
      try {
        if (watch('ta_Wound2_Diagnosis1@odatabind')) {
          setValue('ta_Wound2_Diagnosis1@odatabind', null as any);
        }
        await createDiagnosisCode({ ta_name: searchText });
      } catch (error) {
        // Error is handled in the onError callback
      }
    } else {
      setValue('ta_Wound2_Diagnosis1@odatabind', newValue as SelectItem);
      setError('ta_Wound2_Diagnosis1@odatabind', { type: 'manual', message: '' });
    }
  };

  // Handle selection for diagnosis 2
  const handleDiagnosis2Change = async (newValue: SingleValue<SelectItem>) => {
    if (!newValue) {
      setValue('ta_Wound2_Diagnosis2@odatabind', null as any);
      return;
    }

    if ((newValue as any).__isAddOption && typeof newValue.value === 'string' && newValue.value.startsWith('__add__')) {
      const searchText = newValue.value.replace('__add__', '');
      setActiveDiagnosisField('diagnosis2');
      try {
        if (watch('ta_Wound2_Diagnosis2@odatabind')) {
          setValue('ta_Wound2_Diagnosis2@odatabind', null as any);
        }
        await createDiagnosisCode({ ta_name: searchText });
      } catch (error) {
        // Error is handled in the onError callback
      }
    } else {
      setValue('ta_Wound2_Diagnosis2@odatabind', newValue as SelectItem);
      setError('ta_Wound2_Diagnosis2@odatabind', { type: 'manual', message: '' });
    }
  };

  // Handle selection for diagnosis 3
  const handleDiagnosis3Change = async (newValue: SingleValue<SelectItem>) => {
    if (!newValue) {
      setValue('ta_Wound2_Diagnosis3@odatabind', null as any);
      return;
    }

    if ((newValue as any).__isAddOption && typeof newValue.value === 'string' && newValue.value.startsWith('__add__')) {
      const searchText = newValue.value.replace('__add__', '');
      setActiveDiagnosisField('diagnosis3');
      try {
        if (watch('ta_Wound2_Diagnosis3@odatabind')) {
          setValue('ta_Wound2_Diagnosis3@odatabind', null as any);
        }
        await createDiagnosisCode({ ta_name: searchText });
      } catch (error) {
        // Error is handled in the onError callback
      }
    } else {
      setValue('ta_Wound2_Diagnosis3@odatabind', newValue as SelectItem);
      setError('ta_Wound2_Diagnosis3@odatabind', { type: 'manual', message: '' });
    }
  };

  // Handle selection for diagnosis 4
  const handleDiagnosis4Change = async (newValue: SingleValue<SelectItem>) => {
    if (!newValue) {
      setValue('ta_Wound2_Diagnosis4@odatabind', null as any);
      return;
    }

    if ((newValue as any).__isAddOption && typeof newValue.value === 'string' && newValue.value.startsWith('__add__')) {
      const searchText = newValue.value.replace('__add__', '');
      setActiveDiagnosisField('diagnosis4');
      try {
        if (watch('ta_Wound2_Diagnosis4@odatabind')) {
          setValue('ta_Wound2_Diagnosis4@odatabind', null as any);
        }
        await createDiagnosisCode({ ta_name: searchText });
      } catch (error) {
        // Error is handled in the onError callback
      }
    } else {
      setValue('ta_Wound2_Diagnosis4@odatabind', newValue as SelectItem);
      setError('ta_Wound2_Diagnosis4@odatabind', { type: 'manual', message: '' });
    }
  };

  useEffect(() => {
    const length = watch('ta_wound2_woundlength') || 0;
    const width = watch('ta_wound2_woundwidth') || 0;
    const size = length * width;
    setValue('ta_wound2_woundsizetotal', parseFloat(size.toFixed(2)), { shouldDirty: true });
  }, [watch('ta_wound2_woundlength'), watch('ta_wound2_woundwidth')]);

  return (
    <Box mb={2}>
      <Grid
        templateColumns={{
          base: '1fr',
          sm: 'repeat(1, 1fr)',
          md: 'repeat(3, 1fr)',
        }}
        gap={{ base: 4, sm: 5, md: 4 }}
      >
        <GridItem>
          <FaceliftSelect<SelectItem, false>
            limit={15}
            topLabel="Application Code 1"
            required
            value={
              enhancedItemsCode1.find(
                (item) =>
                  item.value ===
                  (watch('ta_Wound2_Application1@odatabind') as SelectItem)?.value,
              ) || null
            }
            onChange={handleCode1Change}
            onMenuOpen={() => setActiveField('code1')}
            items={enhancedItemsCode1}
            loading={applicationCodes.loading || isCreating}
            error={errors?.['ta_Wound2_Application1@odatabind']?.message}
            placeholder="Select Code"
            onLoadMore={applicationCodes.onLoadMore}
            ai
            aiSuggestionText='Use a keyword like "E10.129" to see more relevant results'
          />
        </GridItem>

        <GridItem>
          <FaceliftSelect<SelectItem, false>
            limit={15}
            topLabel="Application Code 2"
            value={
              enhancedItemsCode2.find(
                (item) =>
                  item.value ===
                  (watch('ta_Wound2_Application2@odatabind') as SelectItem)?.value,
              ) || null
            }
            onChange={handleCode2Change}
            onMenuOpen={() => setActiveField('code2')}
            items={enhancedItemsCode2}
            loading={applicationCodes.loading || isCreating}
            placeholder="Select Code"
            onLoadMore={applicationCodes.onLoadMore}
            ai
            aiSuggestionText='Use a keyword like "E10.129" to see more relevant results'
          />
        </GridItem>

        {/* Row 2 */}
        <GridItem>
          <FaceliftInput
            topLabel="Wound Length (cm)"
            placeholder="Add Length"
            min={0.0}
            value={watch('ta_wound2_woundlength') ?? ''}
            type="number"
            required
            error={errors?.ta_wound2_woundlength?.message}
            onChange={(e) => handleNumericInputChange(e.target.value, 'ta_wound2_woundlength')}
          />
        </GridItem>

        <GridItem>
          <FaceliftInput
            topLabel="Wound Width (cm)"
            placeholder="Add Width"
            min={0.0}
            value={watch('ta_wound2_woundwidth') ?? ''}
            type="number"
            required
            error={errors?.ta_wound2_woundwidth?.message}
            onChange={(e) => handleNumericInputChange(e.target.value, 'ta_wound2_woundwidth')}
          />
        </GridItem>

        <GridItem>
          <FaceliftInput
            topLabel="Wound Size (cm)"
            placeholder="Add Size"
            min={0}
            value={watch('ta_wound2_woundsizetotal') as number}
            type="number"
            disabled
          />
        </GridItem>

        <GridItem>
          <FaceliftInput
            topLabel="Wound Depth (cm)"
            placeholder="Add Depth"
            min={0.0}
            value={watch('ta_wound2_wounddepth') ?? ''}
            type="number"
            error={errors?.ta_wound2_wounddepth?.message}
            onChange={(e) => handleNumericInputChange(e.target.value, 'ta_wound2_wounddepth')}
          />
        </GridItem>

        {/* Row 3 */}
        <GridItem>
          <FaceliftSelect<SelectItem, false>
            limit={15}
            topLabel="Primary Diagnosis"
            value={
              enhancedItemsDiagnosis1.find(
                (item) =>
                  item.value ===
                  (watch('ta_Wound2_Diagnosis1@odatabind') as SelectItem)?.value,
              ) || null
            }
            onChange={handleDiagnosis1Change}
            onMenuOpen={() => setActiveDiagnosisField('diagnosis1')}
            items={enhancedItemsDiagnosis1}
            loading={diagnosisCodes.loading || isCreatingDiagnosis}
            placeholder="Select Diagnosis"
            onLoadMore={diagnosisCodes.onLoadMore}
            ai
            aiSuggestionText='Use a keyword like "E10.129" to see more relevant results'
          />
        </GridItem>

        <GridItem>
          <FaceliftSelect<SelectItem, false>
            limit={15}
            topLabel="Diagnosis code 2"
            value={
              enhancedItemsDiagnosis2.find(
                (item) =>
                  item.value ===
                  (watch('ta_Wound2_Diagnosis2@odatabind') as SelectItem)?.value,
              ) || null
            }
            onChange={handleDiagnosis2Change}
            onMenuOpen={() => setActiveDiagnosisField('diagnosis2')}
            items={enhancedItemsDiagnosis2}
            loading={diagnosisCodes.loading || isCreatingDiagnosis}
            placeholder="Select Code"
            onLoadMore={diagnosisCodes.onLoadMore}
            ai
            aiSuggestionText='Use a keyword like "E10.129" to see more relevant results'
          />
        </GridItem>

        <GridItem>
          <FaceliftSelect<SelectItem, false>
            limit={15}
            topLabel="Diagnosis code 3"
            value={
              enhancedItemsDiagnosis3.find(
                (item) =>
                  item.value ===
                  (watch('ta_Wound2_Diagnosis3@odatabind') as SelectItem)?.value,
              ) || null
            }
            onChange={handleDiagnosis3Change}
            onMenuOpen={() => setActiveDiagnosisField('diagnosis3')}
            items={enhancedItemsDiagnosis3}
            loading={diagnosisCodes.loading || isCreatingDiagnosis}
            placeholder="Select Code"
            onLoadMore={diagnosisCodes.onLoadMore}
            ai
            aiSuggestionText='Use a keyword like "E10.129" to see more relevant results'
          />
        </GridItem>
        <GridItem>
          <FaceliftSelect<SelectItem, false>
            limit={15}
            topLabel="Diagnosis code 4"
            value={
              enhancedItemsDiagnosis4.find(
                (item) =>
                  item.value ===
                  (watch('ta_Wound2_Diagnosis4@odatabind') as SelectItem)?.value,
              ) || null
            }
            onChange={handleDiagnosis4Change}
            onMenuOpen={() => setActiveDiagnosisField('diagnosis4')}
            items={enhancedItemsDiagnosis4}
            loading={diagnosisCodes.loading || isCreatingDiagnosis}
            placeholder="Select Code"
            onLoadMore={diagnosisCodes.onLoadMore}
            ai
            aiSuggestionText='Use a keyword like "E10.129" to see more relevant results'
          />
        </GridItem>
        <GridItem>
          <FaceliftSelect<SelectItem, false>
            limit={15}
            topLabel="Wound Type"
            value={
              woundTypeMap.find(
                (item) =>
                  item.value === (watch('ta_woundtype2') as SelectItem)?.value,
              ) || null
            }
            onChange={(newValue: SingleValue<SelectItem>) =>
              setValue('ta_woundtype2', newValue as SelectItem, { shouldDirty: true })
            }
            items={woundTypeMap}
            placeholder="Select Wound Type"
          />
        </GridItem>
      </Grid>
    </Box>
  );
};

export default Wound2;
